import { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "@/features/authentication";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { reportError } from "@/utils/observability";
import { MapPin, Image, Map as MapIcon } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import {
  SpacesLocationMap,
  isMappable,
  POI_COLORS,
  POI_ICONS,
} from "../../map";
import { POI_CATEGORIES } from "../constants";
import { DEFAULT_MAP_CENTER } from "../constants";
import { visionService } from "@/services/vision-service";
import type {
  LivePreview,
  POICategory,
  PointOfInterest,
  ActiveCombinationPreview,
} from "../types";
import { computeVisibleSpaces } from "../utils/computeVisibleSpaces";
import {
  buildSpacePhotosByBuilding,
  resolveSpacePhotos,
} from "../utils/resolveSpacePhotos";

// ─── PhotosSection ────────────────────────────────────────────────────────────
// A labeled horizontal carousel for a single photo set (room or space).

interface PhotosSectionProps {
  photos: string[];
  variant: "sala" | "espaco";
  categoryLabel: string;
  nameLabel?: string;
  failedImages: Set<string>;
  onImageError: (url: string) => void;
}

export function PhotosSection({
  photos,
  variant,
  categoryLabel,
  nameLabel,
  failedImages,
  onImageError,
}: PhotosSectionProps) {
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [current, setCurrent] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const handleSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", handleSelect);
    return () => {
      api.off("select", handleSelect);
    };
  }, [api]);

  // Sync lightbox navigation with main carousel; keep position on close
  useEffect(() => {
    if (lightboxIdx === null) return;
    api?.scrollTo(lightboxIdx);
  }, [lightboxIdx, api]);

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxIdx(null);
      if (e.key === "ArrowRight")
        setLightboxIdx((i) => Math.min((i ?? 0) + 1, validPhotos.length - 1));
      if (e.key === "ArrowLeft")
        setLightboxIdx((i) => Math.max((i ?? 0) - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const validPhotos = photos.filter((u) => !failedImages.has(u));
  if (validPhotos.length === 0) return null;

  const isSala = variant === "sala";
  const accentColor = isSala ? "#FFD900" : "#C8C4BF";
  const labelColor = isSala ? "#8C7200" : "#9E9A94";
  const lbIdx = lightboxIdx ?? 0;

  return (
    <>
      <div>
        {/* Section header */}
        <div className="flex items-center gap-3 px-4 pt-4 pb-2.5">
          <div
            className="flex-none rounded-full"
            style={{ width: 3, height: 36, background: accentColor }}
          />
          <div className="min-w-0 flex-1">
            <p
              className="font-bold uppercase"
              style={{
                fontSize: 9,
                color: labelColor,
                letterSpacing: "0.11em",
              }}
            >
              {categoryLabel}
            </p>
            {nameLabel && (
              <p
                className="text-sm font-semibold truncate leading-tight mt-0.5"
                style={{ color: "#1A1814" }}
              >
                {nameLabel}
              </p>
            )}
          </div>
          {validPhotos.length > 1 && (
            <span
              className="flex-none text-[10px] tabular-nums"
              style={{ color: "#C8C4BF" }}
            >
              {String(current + 1).padStart(2, "0")} /{" "}
              {String(validPhotos.length).padStart(2, "0")}
            </span>
          )}
        </div>

        {/* Carousel — full-bleed, editorial */}
        <div className="relative">
          <Carousel
            className="w-full"
            setApi={(a) => setApi(a)}
            opts={{ loop: false }}
          >
            <CarouselContent className="-ml-0">
              {validPhotos.map((url, i) => (
                <CarouselItem key={i} className="pl-0">
                  <div
                    className="relative w-full aspect-[16/9] overflow-hidden rounded-lg cursor-zoom-in"
                    onClick={() => setLightboxIdx(i)}
                  >
                    <img
                      src={url}
                      alt={`${nameLabel ?? categoryLabel} ${i + 1}`}
                      className="w-full h-full object-cover"
                      onError={() => onImageError(url)}
                    />
                    {validPhotos.length > 1 && (
                      <div
                        className="absolute inset-x-0 bottom-0 h-12 pointer-events-none"
                        style={{
                          background:
                            "linear-gradient(to top, rgba(10,8,5,0.45) 0%, transparent 100%)",
                        }}
                      />
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>

            {validPhotos.length > 1 && (
              <>
                <CarouselPrevious
                  className="left-3 h-7 w-7 border-0 shadow-md"
                  style={{
                    background: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(4px)",
                    color: "#1A1814",
                  }}
                />
                <CarouselNext
                  className="right-3 h-7 w-7 border-0 shadow-md"
                  style={{
                    background: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(4px)",
                    color: "#1A1814",
                  }}
                />
              </>
            )}
          </Carousel>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center"
          style={{
            background: "rgba(10,8,5,0.92)",
            backdropFilter: "blur(8px)",
          }}
          onClick={() => setLightboxIdx(null)}
        >
          {/* Header */}
          <div
            className="absolute top-0 inset-x-0 flex items-center justify-between px-6 py-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2.5">
              <div
                className="w-0.5 h-6 rounded-full"
                style={{ background: accentColor }}
              />
              <div>
                <p
                  className="font-bold uppercase"
                  style={{
                    fontSize: 9,
                    color: accentColor,
                    letterSpacing: "0.11em",
                  }}
                >
                  {categoryLabel}
                </p>
                {nameLabel && (
                  <p
                    className="text-sm font-semibold"
                    style={{ color: "white" }}
                  >
                    {nameLabel}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span
                className="text-sm tabular-nums"
                style={{ color: "rgba(255,255,255,0.5)" }}
              >
                {lbIdx + 1} / {validPhotos.length}
              </span>
              <button
                type="button"
                onClick={() => setLightboxIdx(null)}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                style={{ background: "rgba(255,255,255,0.1)", color: "white" }}
                aria-label="Fechar"
              >
                ×
              </button>
            </div>
          </div>

          {/* Image */}
          <img
            src={validPhotos[lbIdx]}
            alt={`${nameLabel ?? categoryLabel} ${lbIdx + 1}`}
            className="max-w-[92vw] max-h-[78vh] object-contain rounded-lg"
            style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.6)" }}
            onClick={(e) => e.stopPropagation()}
            onError={() => onImageError(validPhotos[lbIdx])}
          />

          {/* Prev / Next */}
          {validPhotos.length > 1 && (
            <>
              <button
                type="button"
                disabled={lbIdx === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIdx((i) => Math.max((i ?? 0) - 1, 0));
                }}
                className="absolute left-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-20"
                style={{
                  background: "rgba(255,255,255,0.12)",
                  color: "white",
                  fontSize: 22,
                }}
                aria-label="Anterior"
              >
                ‹
              </button>
              <button
                type="button"
                disabled={lbIdx === validPhotos.length - 1}
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIdx((i) =>
                    Math.min((i ?? 0) + 1, validPhotos.length - 1),
                  );
                }}
                className="absolute right-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-20"
                style={{
                  background: "rgba(255,255,255,0.12)",
                  color: "white",
                  fontSize: 22,
                }}
                aria-label="Próxima"
              >
                ›
              </button>
            </>
          )}

          {/* Dot strip */}
          {validPhotos.length > 1 && (
            <div
              className="absolute bottom-6 flex items-center gap-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              {validPhotos.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setLightboxIdx(i)}
                  className="rounded-full transition-all duration-200"
                  style={{
                    width: i === lbIdx ? 20 : 6,
                    height: 6,
                    background:
                      i === lbIdx ? accentColor : "rgba(255,255,255,0.3)",
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

interface LocationPreviewProps {
  preview: LivePreview;
  onSpaceMarkerClick?: (id: string) => void;
}

export default function LocationPreview({
  preview,
  onSpaceMarkerClick,
}: LocationPreviewProps) {
  const { userInfo } = useAuth();
  const [viewMode, setViewMode] = useState<"photos" | "map">("map");
  const carouselApiRef = useRef<CarouselApi | null>(null);

  useEffect(() => {
    setFailedImages(new Set());
    carouselApiRef.current?.scrollTo(0);
  }, [preview.activeSpaceId]);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [activePoiCategories, setActivePoiCategories] = useState<POICategory[]>(
    [],
  );

  const [fetchedPois, setFetchedPois] = useState<PointOfInterest[]>([]);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const allSpaces = preview.rankedSpaces ?? [];
  const allOutdatedSpaces = preview.outdatedSpaces ?? [];
  const visibleSpaces = computeVisibleSpaces(
    allSpaces,
    allOutdatedSpaces,
    preview.showOtherSpaces ?? false,
    preview.showOutdatedSpaces ?? false,
    preview.selectedSpaceIds ?? [],
  );

  const partnerIds = visibleSpaces
    .map((s) => s.partnerId)
    .filter((id): id is number => id != null);
  const partnerIdsKey = partnerIds.join(",");
  useEffect(() => {
    if (!partnerIdsKey) {
      setFetchedPois([]);
      return;
    }
    visionService
      .getPois(partnerIdsKey.split(",").map(Number))
      .then(setFetchedPois)
      .catch((error) => {
        reportError(error, { operation: "getPois" });
        setFetchedPois([]);
      });
  }, [partnerIdsKey]);

  const activeCombination: ActiveCombinationPreview | null =
    preview.activeCombination ?? null;

  const activeSpace =
    !activeCombination && preview.activeSpaceId
      ? ([...allSpaces, ...allOutdatedSpaces].find(
          (s) => s.id === preview.activeSpaceId,
        ) ?? null)
      : null;

  // Index space photos per building so a room missing its own spacePhotos
  // (e.g. reopened from a saved proposal) can borrow them from a sibling room
  // of the same building.
  const spacePhotosByBuilding = useMemo(
    () =>
      buildSpacePhotosByBuilding([
        ...allSpaces,
        ...allOutdatedSpaces,
        ...(preview.extraSpaces ?? []),
      ]),
    [allSpaces, allOutdatedSpaces, preview.extraSpaces],
  );

  // Ungeocoded spaces (null lat/lng) must not become the camera target — fall
  // through to the next mappable candidate instead.
  const firstMappableVisible = visibleSpaces.find(isMappable);
  const mapCenter =
    activeCombination && isMappable(activeCombination)
      ? { lat: activeCombination.lat, lng: activeCombination.lng }
      : activeSpace && isMappable(activeSpace)
        ? { lat: activeSpace.lat, lng: activeSpace.lng }
        : firstMappableVisible
          ? { lat: firstMappableVisible.lat, lng: firstMappableVisible.lng }
          : preview.location?.lat != null && preview.location?.lng != null
            ? { lat: preview.location.lat, lng: preview.location.lng }
            : DEFAULT_MAP_CENTER;

  // Build a unified tagged carousel: room photos first, then space photos
  type TaggedPhoto = { url: string; tag: string };

  const taggedCarouselItems = useMemo((): TaggedPhoto[] => {
    const extraSpacesInPreview = preview.extraSpaces ?? [];
    if (activeCombination) {
      const roomPhotos = activeCombination.rooms.flatMap((room) =>
        room.images
          .filter((img) => !failedImages.has(img))
          .map((url) => ({ url, tag: room.name })),
      );
      // space photos from the first room's spacePhotos (same space for all combo rooms)
      // include extraSpaces (combo-only rooms not in main results)
      const firstRoomSpace = [
        ...allSpaces,
        ...allOutdatedSpaces,
        ...extraSpacesInPreview,
      ].find((s) => s.id === activeCombination.rooms[0]?.id);
      const spacePhotos = resolveSpacePhotos(
        firstRoomSpace,
        spacePhotosByBuilding,
      )
        .filter((img) => !failedImages.has(img))
        .map((url) => ({ url, tag: "Foto do Espaço" }));
      return [...roomPhotos, ...spacePhotos];
    }
    if (activeSpace) {
      const roomPhotos = activeSpace.images
        .filter((img) => !failedImages.has(img))
        .map((url) => ({ url, tag: "Foto da Sala" }));
      const spacePhotos = resolveSpacePhotos(activeSpace, spacePhotosByBuilding)
        .filter((img) => !failedImages.has(img))
        .map((url) => ({ url, tag: "Foto do Espaço" }));
      return [...roomPhotos, ...spacePhotos];
    }
    // No active space: show all visible spaces without tag filtering
    return visibleSpaces
      .flatMap((s) => [
        ...s.images.map((url) => ({ url, tag: "Foto da Sala" })),
        ...resolveSpacePhotos(s, spacePhotosByBuilding).map((url) => ({
          url,
          tag: "Foto do Espaço",
        })),
      ])
      .filter((item) => !failedImages.has(item.url))
      .slice(0, 12);
  }, [
    activeCombination,
    activeSpace,
    visibleSpaces,
    failedImages,
    allSpaces,
    allOutdatedSpaces,
    preview.extraSpaces,
    spacePhotosByBuilding,
  ]);

  const visiblePois =
    activePoiCategories.length > 0
      ? fetchedPois.filter((p) => activePoiCategories.includes(p.category))
      : [];

  function togglePoiCategory(cat: POICategory) {
    setActivePoiCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    );
  }

  const combinationExtraSpaces = preview.extraSpaces ?? [];
  const combinationFirstRoomSpace = activeCombination
    ? [...allSpaces, ...allOutdatedSpaces, ...combinationExtraSpaces].find(
        (s) => s.id === activeCombination.rooms[0]?.id,
      )
    : undefined;
  const combinationSpacePhotos = resolveSpacePhotos(
    combinationFirstRoomSpace,
    spacePhotosByBuilding,
  ).filter((u) => !failedImages.has(u));
  const activeSpacePhotos = resolveSpacePhotos(
    activeSpace,
    spacePhotosByBuilding,
  ).filter((u) => !failedImages.has(u));

  return (
    <div
      className="w-full h-full flex flex-col"
      style={{ background: "#FDFBF7" }}
    >
      <div
        className="flex-none flex items-center justify-between px-5 py-3"
        style={{ borderBottom: "1px solid #E8E6E3" }}
      >
        {preview.location ? (
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: "rgba(255,217,0,0.12)", color: "#1A1814" }}
          >
            <MapPin className="w-3 h-3" />
            {preview.location.label}
            {preview.radiusKm && (
              <span
                className="ml-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                style={{ background: "#FFD900", color: "#1A1814" }}
              >
                {preview.radiusKm} km
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs" style={{ color: "#9E9A94" }}>
            Selecione uma localização
          </span>
        )}

        <div
          className="flex items-center p-0.5 rounded-full"
          style={{ background: "#F0EEEB" }}
        >
          <button
            type="button"
            onClick={() => {
              setViewMode("photos");
              sendTrackDataServer({
                eventName: "VISION_AI_MODE_VIEW_MODE_CHANGED",
                productType: "PO",
                url: window.location.pathname,
                data: {
                  deal_id: null,
                  user_id: userInfo?.user?.id ?? null,
                  email: userInfo?.profile?.email ?? null,
                  view_mode: "photos",
                },
              });
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
            style={
              viewMode === "photos"
                ? { background: "#1A1814", color: "white" }
                : { color: "#78736D" }
            }
          >
            <Image className="w-3.5 h-3.5" />
            Fotos
          </button>
          <button
            type="button"
            onClick={() => {
              setViewMode("map");
              sendTrackDataServer({
                eventName: "VISION_AI_MODE_VIEW_MODE_CHANGED",
                productType: "PO",
                url: window.location.pathname,
                data: {
                  deal_id: null,
                  user_id: userInfo?.user?.id ?? null,
                  email: userInfo?.profile?.email ?? null,
                  view_mode: "map",
                },
              });
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
            style={
              viewMode === "map"
                ? { background: "#1A1814", color: "white" }
                : { color: "#78736D" }
            }
          >
            <MapIcon className="w-3.5 h-3.5" />
            Mapa
          </button>
        </div>
      </div>

      <div className="flex-1 relative min-h-0">
        {viewMode === "map" && (
          <>
            <SpacesLocationMap
              center={mapCenter}
              zoom={activeSpace ? 15 : preview.location ? 13 : 11}
              spaces={visibleSpaces}
              pois={visiblePois}
              highlightedId={highlightedId}
              activeSpaceId={
                activeCombination
                  ? (activeCombination.rooms[0]?.id ?? null)
                  : preview.activeSpaceId
              }
              onSpaceClick={(id) => {
                setHighlightedId(id === highlightedId ? null : id);
                onSpaceMarkerClick?.(id);
              }}
            />
            {!preview.location && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium"
                  style={{
                    background: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(8px)",
                    color: "#78736D",
                  }}
                >
                  <MapPin className="w-4 h-4" />
                  Selecione uma localização nos filtros à direita
                </div>
              </div>
            )}
          </>
        )}

        {viewMode === "photos" && (
          <div
            className="absolute inset-0 overflow-y-auto"
            style={{ scrollbarWidth: "none" }}
          >
            {activeCombination ? (
              // ── Combination: one carousel per room, then shared space photos ──
              <div className="grid grid-cols-2 gap-x-5 gap-y-6 p-4 max-w-[840px] 2xl:max-w-[1428px] mx-auto">
                {activeCombination.rooms.map((room, i) => (
                  <PhotosSection
                    key={room.id}
                    photos={room.images}
                    variant="sala"
                    categoryLabel={
                      activeCombination.rooms.length > 1
                        ? `Sala privativa ${i + 1}`
                        : "Sala privativa"
                    }
                    nameLabel={room.name}
                    failedImages={failedImages}
                    onImageError={(u) =>
                      setFailedImages((p) => new Set([...p, u]))
                    }
                  />
                ))}
                {combinationSpacePhotos.length > 0 && (
                  <PhotosSection
                    photos={combinationSpacePhotos}
                    variant="espaco"
                    categoryLabel="Espaço"
                    nameLabel={combinationFirstRoomSpace?.spaceName}
                    failedImages={failedImages}
                    onImageError={(u) =>
                      setFailedImages((p) => new Set([...p, u]))
                    }
                  />
                )}
              </div>
            ) : activeSpace ? (
              // ── Single active space: room carousel + space carousel ──
              <div className="grid grid-cols-2 gap-x-5 gap-y-6 p-4 max-w-[840px] 2xl:max-w-[1428px] mx-auto">
                <PhotosSection
                  photos={activeSpace.images}
                  variant="sala"
                  categoryLabel="Sala privativa"
                  nameLabel={activeSpace.roomName}
                  failedImages={failedImages}
                  onImageError={(u) =>
                    setFailedImages((p) => new Set([...p, u]))
                  }
                />
                {activeSpacePhotos.length > 0 && (
                  <PhotosSection
                    photos={activeSpacePhotos}
                    variant="espaco"
                    categoryLabel="Espaço"
                    nameLabel={activeSpace.spaceName}
                    failedImages={failedImages}
                    onImageError={(u) =>
                      setFailedImages((p) => new Set([...p, u]))
                    }
                  />
                )}
              </div>
            ) : (
              // ── Overview: flat carousel across all visible spaces ──
              <div className="w-full h-full flex items-center justify-center p-5">
                {taggedCarouselItems.length > 0 ? (
                  <Carousel
                    className="w-full max-w-2xl"
                    setApi={(api) => {
                      carouselApiRef.current = api;
                    }}
                  >
                    <CarouselContent>
                      {taggedCarouselItems.map((item, i) => (
                        <CarouselItem key={i}>
                          <div className="relative aspect-[16/9] overflow-hidden rounded-2xl">
                            <img
                              src={item.url}
                              alt={item.tag}
                              className="w-full h-full object-cover"
                              onError={() =>
                                setFailedImages(
                                  (prev) => new Set([...prev, item.url]),
                                )
                              }
                            />
                            <div className="absolute top-3 left-3">
                              <span
                                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                                style={{
                                  background: "rgba(26,24,20,0.75)",
                                  color:
                                    item.tag === "Foto do Espaço"
                                      ? "#A8A29E"
                                      : "#FFD900",
                                  backdropFilter: "blur(6px)",
                                }}
                              >
                                {item.tag}
                              </span>
                            </div>
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    {taggedCarouselItems.length > 1 && (
                      <>
                        <CarouselPrevious className="left-4" />
                        <CarouselNext className="right-4" />
                      </>
                    )}
                  </Carousel>
                ) : (
                  <div className="text-center">
                    <Image
                      className="w-8 h-8 mx-auto mb-2"
                      style={{ color: "#9E9A94" }}
                    />
                    <p className="text-sm" style={{ color: "#78736D" }}>
                      {preview.location
                        ? "Não há imagens para exibir"
                        : "Selecione uma localização para ver fotos"}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {viewMode === "map" && preview.location && (
        <div
          className="flex-none flex items-center gap-1.5 px-5 py-3 justify-center"
          style={{ borderTop: "1px solid #E8E6E3" }}
        >
          {POI_CATEGORIES.map((cat) => {
            const active = activePoiCategories.includes(cat.value);
            const IconComp = POI_ICONS[cat.value];
            return (
              <button
                key={cat.value}
                type="button"
                onClick={() => togglePoiCategory(cat.value)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-medium transition-all"
                style={
                  active
                    ? {
                        background: POI_COLORS[cat.value],
                        color: "white",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      }
                    : {
                        background: "#F0EEEB",
                        color: "#78736D",
                      }
                }
              >
                <IconComp className="w-3 h-3" />
                {cat.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
